import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Paper, CircularProgress } from '@mui/material';
import { withStyles } from '@mui/styles';
import { withTranslation } from 'react-i18next';
import { COLORS } from '../../../utils/Application_Constants';
import PreferencesTravelComponent from '../PreferencesTravelComponent/PreferencesTravelComponent';
import {
    getClinicianTravelPreferences,
    postClinicianTravelPreferences,
} from '../../../services/ProfileServices/profileService';
import { getConfigurationSetting } from '../../../services/AppConfigService';

const styles = (theme) => ({
    header: {
        color: COLORS.ST_TROPAZ,
        textAlign: 'left',
        paddingTop: '36px',
        fontSize: '17px',
        fontWeight: '700',
    },
    inputLabel: {
        color: COLORS.RAVEN,
        fontWeight: '900',
        fontSize: '12px',
    },
    progress: {
        height: '100vh',
    },
    paperRoot: {
        paddingBottom: '60px',
    },
    gridRoot: {
        [theme.breakpoints.up('md')]: {
            display: 'flex',
            padding: '0 32px',
        },
        [theme.breakpoints.down('md')]: {
            padding: '5px 10px',
        },
    },
    setWidthTo100: {
        width: '100%',
    },

    typeAheadInput: {
        'label + &': {
            marginTop: theme.spacing(2),
        },
        borderRadius: 3,
        position: 'relative',
        backgroundColor: COLORS.WHITE,
        color: COLORS.RAVEN,
        border: `1px solid ${COLORS.WHITE_LILAC}`,
        fontSize: 16,
        width: '100%',
        transition: theme.transitions.create(['border-color', 'box-shadow']),
        '&:focus': {
            boxShadow: COLORS.WHITE_LILAC,
            borderColor: COLORS.RAVEN,
        },
    },
    chipRoot: {
        backgroundColor: COLORS.ZIRCON,
        borderRadius: '3px',
    },
    chipLabel: {
        color: COLORS.RAVEN,
    },
    disabledInput: {
        opacity: '0.6',
    },
});

class TravelPreferences extends Component {
    constructor(props) {
        super(props);
        this.state = {
            clinicianPreferences: {},
            originalClinicianPreferences: {},
            workInterestArray: [],
            emailSubscribeflag: false,
            showSmsPreferences: false,
        };
    }

    async componentDidMount() {
        const { externalId, setSaveHandler, setPreferencePageError } = this.props;

        try {
            const [clinicianPreferences, showSmsPreferences] = await Promise.all([
                getClinicianTravelPreferences(externalId).catch((e) => {
                    setPreferencePageError(e);
                    return null;
                }),
                getConfigurationSetting('ShowSmsPreferences', true, false),
            ]);

            // Normalize dates to match handler format before storing
            const normalized = this.normalizeDateFields(clinicianPreferences);
            this.setState({
                clinicianPreferences: normalized,
                originalClinicianPreferences: Object.freeze(JSON.parse(JSON.stringify(normalized))),
                showSmsPreferences,
            });
        } catch (error) {
            console.error('Unexpected error during componentDidMount:', error);
        }

        this.shouldDisableSave();
        setSaveHandler(this, this.doSubmit);
    }

    componentDidUpdate() {
        const { clinicianPreferences } = this.state;

        if (clinicianPreferences) {
            this.updateDirtyState();
        }
    }

    normalizeDateFields = (prefs) => {
        if (!prefs) return prefs;
        const normalized = { ...prefs };
        // Normalize knownTravellerNumberExpirationDate from API format to YYYY-MM-DD
        if (
            normalized.knownTravellerNumberExpirationDate &&
            typeof normalized.knownTravellerNumberExpirationDate === 'string'
        ) {
            [normalized.knownTravellerNumberExpirationDate] =
                normalized.knownTravellerNumberExpirationDate.split('T');
        }
        return normalized;
    };

    arraysEqualIgnoreOrder = (arr1, arr2, sortKey) => {
        if (!arr1 && !arr2) return true;
        if (!arr1 || !arr2) return false;
        if (arr1.length !== arr2.length) return false;

        // Sort both arrays by the sort key
        const sorted1 = [...arr1].sort((a, b) => {
            const valA = a[sortKey] || '';
            const valB = b[sortKey] || '';
            return valA.localeCompare(valB);
        });

        const sorted2 = [...arr2].sort((a, b) => {
            const valA = a[sortKey] || '';
            const valB = b[sortKey] || '';
            return valA.localeCompare(valB);
        });

        // Compare each element
        return sorted1.every(
            (item1, idx) => JSON.stringify(item1) === JSON.stringify(sorted2[idx])
        );
    };

    preferencesAreEqual = (prefs1, prefs2) => {
        if (!prefs1 || !prefs2) return prefs1 === prefs2;

        // Check rentalServices array (order-independent)
        if (
            !this.arraysEqualIgnoreOrder(
                prefs1.rentalServices,
                prefs2.rentalServices,
                'rentalCompany'
            )
        ) {
            return false;
        }

        // Check hotels array (order-independent)
        if (!this.arraysEqualIgnoreOrder(prefs1.hotels, prefs2.hotels, 'hotelName')) {
            return false;
        }

        // Check airlinePreferences array (order matters)
        if (
            JSON.stringify(prefs1.airlinePreferences) !== JSON.stringify(prefs2.airlinePreferences)
        ) {
            return false;
        }

        // Check all other scalar fields
        const scalarFields = [
            'clinicianId',
            'legalFirstName',
            'legalMiddleInitial',
            'legalLastName',
            'petTravel',
            'petType',
            'petBreed',
            'petWeight',
            'seatPreference',
            'knownTravellerNumber',
        ];

        const dateFields = ['knownTravellerNumberExpirationDate'];

        // Compare scalar fields
        const scalarFieldDiffers = scalarFields.some((field) => {
            if (prefs1[field] !== prefs2[field]) {
                return true;
            }
            return false;
        });

        if (scalarFieldDiffers) return false;

        // Compare date fields with normalization
        const dateFieldDiffers = dateFields.some((field) => {
            const val1 = prefs1[field];
            const val2 = prefs2[field];

            // Both null/undefined
            if (!val1 && !val2) return false;

            // One is null/undefined but not the other
            if (!val1 || !val2) return true;

            // Normalize to ISO string for comparison
            const str1 =
                typeof val1 === 'string'
                    ? val1
                    : val1.toISOString?.() || val1.format?.('YYYY-MM-DD');
            const str2 =
                typeof val2 === 'string'
                    ? val2
                    : val2.toISOString?.() || val2.format?.('YYYY-MM-DD');

            return str1 !== str2;
        });

        if (dateFieldDiffers) return false;

        return true;
    };

    updateDirtyState = () => {
        const { clinicianPreferences, originalClinicianPreferences } = this.state;
        if (!originalClinicianPreferences) return;

        const hasChanges = !this.preferencesAreEqual(
            clinicianPreferences,
            originalClinicianPreferences
        );
        const { flags, updateFlags } = this.props;

        if (hasChanges === flags.isDirty) return;

        updateFlags({ ...flags, isDirty: hasChanges });
    };

    shouldDisableSave = () => {
        const { flags, updateFlags } = this.props;
        const state = { ...this.state };
        const tooManyCharacters =
            (!!state.clinicianPreferences.legalFirstName &&
                state.clinicianPreferences.legalFirstName.length > 50) ||
            (!!state.clinicianPreferences.legalMiddleInitial &&
                state.clinicianPreferences.legalMiddleInitial.length > 1) ||
            (!!state.clinicianPreferences.legalLastName &&
                state.clinicianPreferences.legalLastName.length > 50) ||
            (!!state.clinicianPreferences.knownTravellerNumber &&
                state.clinicianPreferences.knownTravellerNumber.length > 20) ||
            this.loopPreferencesLengthCheck('airlinePreferences', 'frequentFlyerNumber', 20) ||
            this.loopPreferencesLengthCheck('rentalServices', 'membershipNumber', 20) ||
            this.loopPreferencesLengthCheck('hotels', 'hotelName', 50) ||
            this.loopPreferencesLengthCheck('hotels', 'membershipNumber', 20) ||
            (!!state.clinicianPreferences.petType &&
                state.clinicianPreferences.petType.length > 100) ||
            (!!state.clinicianPreferences.petBreed &&
                state.clinicianPreferences.petBreed.length > 100) ||
            (!!state.clinicianPreferences.petWeight &&
                state.clinicianPreferences.petWeight.length > 20);
        flags.isCharacterLimit = tooManyCharacters;
        updateFlags(flags);
    };

    loopPreferencesLengthCheck = (travelGroup, field, limit) => {
        const state = { ...this.state };
        for (let i = 0; i < state.clinicianPreferences[travelGroup].length; i++) {
            if (
                !!state.clinicianPreferences[travelGroup][i][field] &&
                state.clinicianPreferences[travelGroup][i][field].length > limit
            ) {
                return true;
            }
        }
        return false;
    };

    markFlagsDirty = () => {
        const { flags, updateFlags } = this.props;
        updateFlags({ ...flags, dataSaved: false });
    };

    airlineConstructor = () => ({
        isPreferred: false,
        airlineName: '',
        frequentFlyerNumber: '',
        knownTravellerNumber: '',
        seatPreference: '',
        expirationDate: null,
    });

    hotelConstructor = () => ({
        isPreferred: false,
        hotelName: '',
        membershipNumber: '',
    });

    /**
     * Adds an ariline to the airline preferences array
     */
    addAirlineClickEvent = () => {
        const state = { ...this.state };
        state.clinicianPreferences.airlinePreferences.push(this.airlineConstructor());
        this.markFlagsDirty();
        this.setState({ clinicianPreferences: state.clinicianPreferences });
    };

    /**
     * Adds a hotel to the hotel preferences array.
     */
    addHotelClickEvent = () => {
        const state = { ...this.state };
        state.clinicianPreferences.hotels.push(this.hotelConstructor());
        this.markFlagsDirty();
        this.setState({ clinicianPreferences: state.clinicianPreferences });
    };

    /**
     * On change handler for input fields in the component
     * @param {MouseEvent} event
     * @param {Number} index
     * @param {String} travelGroup
     */
    travelGroupHandler = (event, index, travelGroup) => {
        const state = { ...this.state };
        const { name, value } = event.target;
        state.clinicianPreferences[travelGroup][index][name] = value;
        this.markFlagsDirty();
        this.shouldDisableSave();
        this.setState({ clinicianPreferences: state.clinicianPreferences });
    };

    /**
     * Rental car companies need a unique handler incase
     * the user selects other. Other will trigger the other
     * field in the rental car company.
     * @param {MouseEvent} event
     * @param {Number} index
     */
    rentalCarCompanyHandler = (event, index) => {
        const state = { ...this.state };
        state.clinicianPreferences.rentalServices[index].rentalCompany = event.target.value;
        this.markFlagsDirty();
        this.setState({ clinicianPreferences: state.clinicianPreferences });
    };

    /**
     * Handler that removes the selected preference when toggled.
     * @param {Number} index
     * @param {String} travelGroup
     */
    removeSelectedPreferenceByTravelGroup = (index, travelGroup) => {
        const state = { ...this.state };
        if (state.clinicianPreferences[travelGroup][index].isPreferred) {
            state.clinicianPreferences[travelGroup][0].isPreferred = true;
        }
        if (travelGroup === 'airlinePreferences') {
            state.clinicianPreferences.airlinePreferences[index].airlineName = null;
        } else if (travelGroup === 'hotels') {
            state.clinicianPreferences.hotels[index].hotelName = null;
        } else {
            state.clinicianPreferences[travelGroup].splice(index, 1);
        }
        this.markFlagsDirty();
        this.setState({ clinicianPreferences: state.clinicianPreferences });
    };

    /**
     * There can only be one preferred item in a group. This method checks whether or not
     * another travel item has been set to preferred and removes the previous flag.
     * @param {Number} the currently selected index;
     * @param {String} the group currenyl being toggled
     */
    preferredGroupToggled = (currentlyToggledIndex, travelGroup) => {
        const state = { ...this.state };
        state.clinicianPreferences[travelGroup].forEach((item, index) => {
            if (index === currentlyToggledIndex) {
                return;
            }
            state.clinicianPreferences[travelGroup][index].isPreferred = false;
        });
        this.markFlagsDirty();
        this.setState({ clinicianPreferences: state.clinicianPreferences });
    };

    /**
     * When clicking a preference this logic ensures that theres always
     * a preference selected
     * @param {Number} index
     * @param {String} travelGroup
     */
    handlePreferredButtonClick = (index, travelGroup) => {
        this.setState((prevState) => {
            const newPreferences = { ...prevState.clinicianPreferences };
            const group = [...newPreferences[travelGroup]];

            group[index] = {
                ...group[index],
                isPreferred: !group[index].isPreferred,
            };

            newPreferences[travelGroup] = group;

            setTimeout(() => {
                this.preferredGroupToggled(index, travelGroup);
                this.markFlagsDirty();
            });

            return { clinicianPreferences: newPreferences };
        });
    };

    async airlinePreferenceValidator() {
        const state = { ...this.state };
        if (state.clinicianPreferences.airlinePreferences.length > 3) {
            const newPreferences = state.clinicianPreferences.airlinePreferences.filter(
                (item) => item.airlineName
            );
            state.clinicianPreferences.airlinePreferences = newPreferences;
            this.shouldDisableSave();
            await this.setState({ clinicianPreferences: state.clinicianPreferences });
        }
    }

    async hotelPreferenceValidator() {
        const state = { ...this.state };
        if (state.clinicianPreferences.hotels.length > 3) {
            const newPreferences = state.clinicianPreferences.hotels.filter(
                (item) => item.hotelName
            );
            state.clinicianPreferences.hotels = newPreferences;
            this.shouldDisableSave();
            await this.setState({ clinicianPreferences: state.clinicianPreferences });
        }
    }

    async validatePreferences() {
        await this.airlinePreferenceValidator();
        await this.hotelPreferenceValidator();
    }

    async doSubmit() {
        // Validate required fields
        const state = { ...this.state };
        const { flags, updateFlags } = this.props;
        const { externalId, setPreferencePageError } = this.props;
        const missingAnyFields = false;
        flags.missingData = missingAnyFields;
        if (!missingAnyFields) {
            flags.isPendingSave = true;
            updateFlags(flags);
            await this.validatePreferences();
            // Clone preferences BEFORE await so we have a snapshot of what was sent
            const preferencesToSave = JSON.parse(JSON.stringify(state.clinicianPreferences));
            try {
                await postClinicianTravelPreferences(externalId, preferencesToSave);
                // Only update snapshot and clear dirty flags if save succeeded
                this.setState({
                    originalClinicianPreferences: Object.freeze(preferencesToSave),
                });
                flags.dataSaved = true;
                flags.isDirty = false;
                flags.isPendingSave = false;
                updateFlags(flags);
            } catch (e) {
                // Save failed - keep isDirty and dataSaved unchanged so user can retry
                setPreferencePageError(e);
                flags.isPendingSave = false;
                updateFlags(flags);
            }
        }
    }

    render() {
        const { classes } = this.props;
        const { clinicianPreferences, showSmsPreferences } = this.state;
        if (Object.keys(clinicianPreferences).length === 0) {
            return (
                <div className={classes.progress}>
                    <CircularProgress />
                </div>
            );
        }
        const { airlinePreferences, rentalServices, hotels } = clinicianPreferences;

        return (
            <Paper classes={{ root: classes.paperRoot }}>
                <PreferencesTravelComponent
                    clinicianPreferences={clinicianPreferences}
                    airlinePreferences={airlinePreferences}
                    rentalServices={rentalServices}
                    hotels={hotels}
                    shouldDisableSave={this.shouldDisableSave}
                    addAirlineClickEvent={this.addAirlineClickEvent}
                    addHotelClickEvent={this.addHotelClickEvent}
                    travelGroupHandler={this.travelGroupHandler}
                    removeSelectedPreferenceByTravelGroup={
                        this.removeSelectedPreferenceByTravelGroup
                    }
                    handlePreferredButtonClick={this.handlePreferredButtonClick}
                    rentalCarCompanyHandler={this.rentalCarCompanyHandler}
                    showSmsPreferences={showSmsPreferences}
                />
            </Paper>
        );
    }
}

TravelPreferences.propTypes = {
    classes: PropTypes.shape({
        header: PropTypes.string.isRequired,
    }).isRequired,
    flags: PropTypes.shape({
        missingData: PropTypes.bool.isRequired,
        dataSaved: PropTypes.bool.isRequired,
        isDirty: PropTypes.bool.isRequired,
        showSaveChanges: PropTypes.bool.isRequired,
        isPendingSave: PropTypes.bool.isRequired,
        targetTab: PropTypes.number.isRequired,
    }).isRequired,
    updateFlags: PropTypes.func.isRequired,
    setSaveHandler: PropTypes.func.isRequired,
    externalId: PropTypes.string.isRequired,
};

export default withTranslation()(withStyles(styles)(TravelPreferences));
